import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Any
import kotlin.Unit
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.renderComposable
import sample.components.layouts.MarkdownLayout as _sample_components_layouts_MarkdownLayout
import sample.components.layouts.PageLayout as _sample_components_layouts_PageLayout
import sample.pages.AboutPage as _sample_pages_AboutPage
import sample.pages.DemoPage as _sample_pages_DemoPage
import sample.pages.HomePage as _sample_pages_HomePage

private val LayoutScopeLocal: ProvidableCompositionLocal<Any?> = compositionLocalOf<Any?> { null }

@Composable
private fun <T : Any> currentLayoutScope(): T {
    @Suppress("UNCHECKED_CAST")
    return LayoutScopeLocal.current as? T
        ?: error("Unexpected between Page/Layout scope and the parent Layout which it is providing it. Please report this issue to the Kobweb developers.")
}

@Composable
private fun <T : Any> provideLayoutScope(layoutScope: T, content: @Composable () -> Unit) {
    androidx.compose.runtime.CompositionLocalProvider(LayoutScopeLocal provides layoutScope, content = content)
}

public fun main() {
    AppGlobals.initialize(mapOf("title" to "Sample"))
    BasePath.set("/kmp-multimodule-sample/")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.registerLayout(
            "sample.components.layouts.MarkdownLayout",
            parentLayoutId = "sample.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                sample.components.layouts.initMarkdownLayout(ctx)
            },
        ) { pageCtx, pageMethod -> 
            _sample_components_layouts_MarkdownLayout {
                pageMethod(pageCtx)
            }
        }
        ctx.router.registerLayout(
            "sample.components.layouts.PageLayout",
        ) { pageCtx, pageMethod -> 
            _sample_components_layouts_PageLayout(pageCtx) {
                provideLayoutScope(this) {
                    pageMethod(pageCtx)
                }
            }
        }
        ctx.router.register(
            "/",
            layoutId = "sample.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                sample.pages.initHomePage(ctx)
            },
        ) { pageCtx -> 
            _sample_pages_HomePage()
        }
        ctx.router.register(
            "/about",
            layoutId = "sample.components.layouts.MarkdownLayout",
            initRouteMethod = { ctx ->
                sample.pages.initAboutPage(ctx)
            },
        ) { pageCtx -> 
            _sample_pages_AboutPage()
        }
        ctx.router.register(
            "/demo",
            layoutId = "sample.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                sample.pages.initDemoPage(ctx)
            },
        ) { pageCtx -> 
            _sample_pages_DemoPage()
        }

    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("headline-text", sample.HeadlineTextStyle)
        ctx.theme.registerStyle("subheadline-text", sample.SubheadlineTextStyle)
        ctx.theme.registerStyle("hero-container", sample.pages.HeroContainerStyle)
        ctx.theme.registerStyle("home-grid", sample.pages.HomeGridStyle)
        ctx.theme.registerStyle("home-grid-cell", sample.pages.HomeGridCellStyle)
        ctx.theme.registerStyle("page-content", sample.components.layouts.PageContentStyle)
        ctx.theme.registerStyle("markdown", sample.components.layouts.MarkdownStyle)
        ctx.theme.registerStyle("footer", sample.components.sections.FooterStyle)
        ctx.theme.registerStyle("nav-header", sample.components.sections.NavHeaderStyle)
        ctx.theme.registerVariant("-circle", sample.CircleButtonVariant
        )
        ctx.theme.registerVariant("-uncolored", sample.UncoloredButtonVariant
        )
        ctx.theme.registerKeyframes("side-menu-slide-in", sample.components.sections.SideMenuSlideInAnim)
        sample.initSiteStyles(ctx)
        sample.initColorMode(ctx)
        sample.initStyles(ctx)
        sample.initTheme(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo(BasePath.remove("/" + window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        sample.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
